import useComponentDidUnmount from 'App/components/hooks/useComponentDidUnmount';
import TierUpgradeRequestResource from 'App/resources/TierUpgradeRequests';
import { Text, Flex, Title, StateFrame, Icon } from '@upsales/components';
import { CancelablePromise, makeCancelable } from 'Helpers/promise';
import { Status } from 'App/resources/Model/TierUpgradeRequest';
import { currencyFormat } from 'Components/Filters/Currencies';
import { useTranslation } from 'Components/Helpers/translate';
import { PrimaryButton } from '@upsales/components/Buttons';
import BemClass from '@upsales/components/Utils/bemClass';
import NotificationService from 'App/babel/NotificationService';
import { useSelf } from 'App/components/hooks/appHooks';
import React, { useRef, useState } from 'react';
import logError from 'Helpers/logError';

import './RequestNextTier.scss';

type TierUpgradeType = {
	currentTier: number;
	potentialTier: number;
	currentYearlyCost: number;
	potentialYearlyCost: number;
	yearlyDiff: number;
	addon: {
		tiers: {
			start: number;
			end: number;
			isTotalPrice: boolean;
			maxDiscount: unknown;
			price: number;
		}[];
		tier: {
			start: number;
			end: number;
			isTotalPrice: boolean;
			maxDiscount: unknown;
			price: number;
			level: number;
		};
		currency: string;
	};
};

const TierCard = ({
	backgroundColor,
	tierQuantity,
	textColor,
	currency,
	tierEnd,
	title,
	arr,
	addon
}: {
	textColor?: React.ComponentProps<typeof Text>['color'];
	backgroundColor?: 'grey-1';
	tierQuantity: number;
	currency: string;
	tierEnd: number;
	title: string;
	arr: number;
	addon: 'CDH' | 'MA';
}) => {
	const classes = new BemClass('RequestNextTier').elem('upgradeInfo').elem('tierCards').elem('tierCard');
	const { t } = useTranslation();
	// The decision was made to show the monthly recurring revenue instead of the annual recurring revenue
	const mrr = arr / 12;

	const tierLimitText =
		addon === 'MA'
			? t('billing.approveTier.upToContacts', { count: tierEnd })
			: t('billing.approveTier.upToCredits', { count: tierEnd });

	return (
		<StateFrame
			state="info"
			className={classes.mod(backgroundColor ?? '').b()}
			title={
				<Flex direction="column" gap="u1">
					<Flex direction="column">
						<Text color={textColor} size="sm">
							{title}
						</Text>
						<Text color={textColor}>{t('product.tier') + ' ' + tierQuantity}</Text>
					</Flex>
					<Flex direction="column">
						<Text color={textColor}>
							{currencyFormat(mrr, currency) + ' / ' + t('date.month').toLowerCase()}
						</Text>
						<Text color={textColor} size="sm">
							{tierLimitText}
						</Text>
					</Flex>
				</Flex>
			}
		></StateFrame>
	);
};

const RequestNextTier = ({
	tierUpgrades,
	approvedTier,
	space = 'ptl',
	hideTitle = false,
	addon = 'CDH'
}: {
	tierUpgrades: TierUpgradeType[];
	approvedTier: () => void;
	space?: string;
	hideTitle?: boolean;
	addon?: 'CDH' | 'MA';
}) => {
	const [requestSent, setRequestSent] = useState(false);
	const self = useSelf();
	const upgradeParam = addon === 'MA' ? 'allowMarketingAutomationTierUpgrade' : 'allowProspectingTierUpgrade';
	const canApproveTierUpgrade = (self?.billingAdmin || self?.userParams?.[upgradeParam]) ?? false;
	const { t } = useTranslation();
	const tierUpgradeRequest = useRef<CancelablePromise<
		Awaited<ReturnType<typeof TierUpgradeRequestResource.save>>
	> | null>(null);

	useComponentDidUnmount(() => {
		tierUpgradeRequest.current?.cancel();
	});
	const [tierUpgrade] = tierUpgrades;

	const sendRequestToBillingAdmin = () => {
		setRequestSent(true);
		tierUpgradeRequest.current = makeCancelable(
			TierUpgradeRequestResource.save({
				requestedTier: tierUpgrade.potentialTier,
				currentTier: tierUpgrade.currentTier,
				addon
			})
		);
		tierUpgradeRequest.current.promise.catch(err => {
			logError(err, 'Failed to send request to billing admin');
			NotificationService.add({
				icon: 'times',
				style: NotificationService.style.ERROR,
				title: 'billing.requestNextTier.requestFailed'
			});
			setRequestSent(false);
		});
	};

	const goUpInTier = () => {
		tierUpgradeRequest.current = makeCancelable(
			TierUpgradeRequestResource.save({
				requestedTier: tierUpgrade.potentialTier,
				currentTier: tierUpgrade.currentTier,
				status: Status.APPROVED,
				addon
			})
		);
		tierUpgradeRequest.current.promise
			.then(() => {
				approvedTier();
			})
			.catch(err => {
				logError(err, 'Failed to approve tier upgrade');
				NotificationService.add({
					icon: 'times',
					style: NotificationService.style.ERROR,
					title: 'billing.approveTier.approveFailed'
				});
			});
	};

	const classes = new BemClass('RequestNextTier');

	const sortedTierUpgrades = tierUpgrade.addon.tiers.sort((a, b) => a.start - b.start);

	return (
		<Flex alignItems="flex-start" direction="column" className={classes.b() + ' max600w'} space={space}>
			{hideTitle ? null : (
				<Title>
					{requestSent
						? t('admin.billing.addon.askAdminRequestSent')
						: t('billing.requestNextTier.notEnoughCredits')}
				</Title>
			)}

			{canApproveTierUpgrade ? (
				<Flex direction="column" gap="u4" className={classes.elem('upgradeInfo').b()}>
					<Text space={hideTitle ? undefined : 'ptm'}>
						{addon === 'MA'
							? t('billing.approveTier.upgradeMA')
							: t('billing.approveTier.upgrade')}
					</Text>
					<Flex alignItems="center" gap="u1" className={classes.elem('upgradeInfo').elem('tierCards').b()}>
						<TierCard
							title={t('default.current')}
							backgroundColor="grey-1"
							arr={tierUpgrade.currentYearlyCost}
							tierEnd={tierUpgrade.addon.tier.end}
							currency={tierUpgrade.addon.currency}
							tierQuantity={tierUpgrade.currentTier}
							addon={addon}
						/>
						<Icon name="arrow-right" />
						<TierCard
							title={t('billing.approveTier.upcoming')}
							textColor="blue"
							arr={tierUpgrade.potentialYearlyCost}
							currency={tierUpgrade.addon.currency}
							tierQuantity={tierUpgrade.potentialTier}
							tierEnd={sortedTierUpgrades[tierUpgrade.potentialTier - 1]?.end ?? 0}
							addon={addon}
						/>
					</Flex>
					<PrimaryButton onClick={goUpInTier} icon="check">
						{t('billing.approveTier.approve')}
					</PrimaryButton>
				</Flex>
			) : (
				<>
					<Text space={hideTitle ? 'mbl' : 'mtm mbl'} color={hideTitle ? undefined : 'grey-11'}>
						{requestSent
							? t('billing.requestNextTier.youWillBeNotified')
							: addon === 'MA'
							? t('billing.requestNextTier.billingAdminMustApproveMA')
							: t('billing.requestNextTier.billingAdminMustApprove')}
					</Text>
					<PrimaryButton
						className={classes.elem('requestButton').b()}
						icon={requestSent ? 'check' : 'email'}
						onClick={sendRequestToBillingAdmin}
						disabled={requestSent}
					>
						{requestSent
							? t('admin.billing.addon.askAdminRequestSent')
							: t('billing.requestNextTier.sendRequestToBillingAdmin')}
					</PrimaryButton>
				</>
			)}
		</Flex>
	);
};

export default RequestNextTier;
